import React, { useCallback, useRef, useState } from 'react';

import { ELayoutManagerMode, type EWidgetSection } from '#shared';

import { useToast } from '../../LayoutManager';
import {
  blockActions,
  sectionActions,
  sectionSelectors,
  widgetInstanceActions,
  useSelectWidgetInstanceEntities,
} from '../../slices';
import { toggleNewCasesBlocks } from '../../thunks';
import { type WidgetId, widgetRegistry } from '../../widgetManager';

import {
  extractWidgetDefaults,
  extractBaseWidgetType,
  generateWidgetId,
} from './utils';

import type { AppDispatch } from '../../redux-store';

type PendingPlacement = {
  sectionId: EWidgetSection;
  position: number;
} | null;

interface UseTapHandlersProps {
  mode: ELayoutManagerMode;
  dispatch: AppDispatch;
  sectionsById: ReturnType<typeof sectionSelectors.useSelectSectionEntities>;
  onDeleteLastWidget?: () => void;
}

export const useTapHandlers = ({
  mode,
  dispatch,
  sectionsById,
  onDeleteLastWidget,
}: UseTapHandlersProps) => {
  const widgetInstances = useSelectWidgetInstanceEntities();
  const { showToast } = useToast();

  const [selectedWidgetId, setSelectedWidgetIdState] =
    useState<WidgetId | null>(null);
  const [selectedPaletteWidget, setSelectedPaletteWidgetState] = useState<
    string | null
  >(null);
  const [pendingPlacement, setPendingPlacementState] =
    useState<PendingPlacement>(null);

  // Refs for stale closure avoidance
  const selectedPaletteWidgetRef = useRef(selectedPaletteWidget);
  const pendingPlacementRef = useRef(pendingPlacement);

  React.useEffect(() => {
    selectedPaletteWidgetRef.current = selectedPaletteWidget;
  }, [selectedPaletteWidget]);

  React.useEffect(() => {
    pendingPlacementRef.current = pendingPlacement;
  }, [pendingPlacement]);

  const setPendingPlacement = useCallback((placement: PendingPlacement) => {
    pendingPlacementRef.current = placement;
    setPendingPlacementState(placement);
  }, []);

  const setSelectedPaletteWidgetStateSync = useCallback(
    (widgetId: string | null) => {
      selectedPaletteWidgetRef.current = widgetId;
      setSelectedPaletteWidgetState(widgetId);
    },
    [],
  );

  const handleDeleteWidget = useCallback(
    (widgetId: WidgetId) => {
      if (mode === ELayoutManagerMode.RENDER) {
        return;
      }

      // Check if this is the last widget in its section
      if (sectionsById) {
        for (const section of Object.values(sectionsById)) {
          if (
            section?.blockOrder?.includes(widgetId) &&
            section.blockOrder.length <= 1
          ) {
            onDeleteLastWidget?.();
            return;
          }
        }
      }

      dispatch(blockActions.blockRemove(widgetId));

      if (selectedWidgetId === widgetId) {
        setSelectedWidgetIdState(null);
      }
    },
    [selectedWidgetId, dispatch, mode, sectionsById, onDeleteLastWidget],
  );

  const handleTapSwap = useCallback(
    (sourceWidgetId: WidgetId, targetWidgetId: WidgetId) => {
      if (mode !== ELayoutManagerMode.EDIT || !sectionsById) {
        return;
      }

      // Find source section
      let sourceSectionId: EWidgetSection | null = null;
      for (const [sectionId, section] of Object.entries(sectionsById)) {
        if (section?.blockOrder?.includes(sourceWidgetId)) {
          sourceSectionId = sectionId as EWidgetSection;
          break;
        }
      }

      // Find target section
      let targetSectionId: EWidgetSection | null = null;
      for (const [sectionId, section] of Object.entries(sectionsById)) {
        if (section?.blockOrder?.includes(targetWidgetId)) {
          targetSectionId = sectionId as EWidgetSection;
          break;
        }
      }

      if (!sourceSectionId || !targetSectionId) {
        return;
      }

      // Don't swap with itself
      if (sourceWidgetId === targetWidgetId) {
        return;
      }

      const sourceSection = sectionsById[sourceSectionId];
      const targetSection = sectionsById[targetSectionId];
      const sourceIndex =
        sourceSection?.blockOrder.indexOf(sourceWidgetId) ?? -1;
      const targetIndex =
        targetSection?.blockOrder.indexOf(targetWidgetId) ?? -1;

      if (
        !sourceSection ||
        !targetSection ||
        sourceIndex === -1 ||
        targetIndex === -1
      ) {
        return;
      }

      // VALIDATION: Check geo-map placement rules
      const sourceWidget = widgetInstances[sourceWidgetId];
      const targetWidget = widgetInstances[targetWidgetId];

      if (
        sourceWidget?.type === 'geo-map' &&
        sourceSectionId === 'center' &&
        (targetSectionId === 'left-primary' ||
          targetSectionId === 'right-primary')
      ) {
        showToast('Cannot move Main Map to side panels.', 'error');
        return;
      }

      if (
        sourceWidget?.type === 'geo-map' &&
        targetSectionId === 'center' &&
        (sourceSectionId === 'left-primary' ||
          sourceSectionId === 'right-primary')
      ) {
        showToast('Cannot move Focus View to center.', 'error');
        return;
      }

      if (
        targetWidget?.type === 'geo-map' &&
        targetSectionId === 'center' &&
        (sourceSectionId === 'left-primary' ||
          sourceSectionId === 'right-primary')
      ) {
        showToast(
          `Cannot swap out ${targetWidget.title} from center. The Main Map must remain in the center panel.`,
          'error',
        );
        return;
      }

      if (
        targetWidget?.type === 'geo-map' &&
        (targetSectionId === 'left-primary' ||
          targetSectionId === 'right-primary') &&
        sourceSectionId === 'center'
      ) {
        showToast(
          `Cannot swap ${targetWidget.title} to center. Use the Main Map widget for the center panel.`,
          'error',
        );
        return;
      }

      // Perform the swap
      const sourceBlockOrder = [...sourceSection.blockOrder];
      const targetBlockOrder =
        sourceSectionId === targetSectionId
          ? sourceBlockOrder
          : [...targetSection.blockOrder];

      sourceBlockOrder[sourceIndex] = targetWidgetId;
      if (sourceSectionId === targetSectionId) {
        sourceBlockOrder[targetIndex] = sourceWidgetId;
      } else {
        targetBlockOrder[targetIndex] = sourceWidgetId;
      }

      dispatch(
        sectionActions.sectionSetBlocks({
          sectionId: sourceSectionId,
          blockIds: sourceBlockOrder,
        }),
      );

      if (sourceSectionId !== targetSectionId) {
        dispatch(
          sectionActions.sectionSetBlocks({
            sectionId: targetSectionId,
            blockIds: targetBlockOrder,
          }),
        );
        dispatch(
          widgetInstanceActions.setWidgetInstance({
            id: sourceWidgetId,
            sectionId: targetSectionId,
          }),
        );
        dispatch(
          widgetInstanceActions.setWidgetInstance({
            id: targetWidgetId,
            sectionId: sourceSectionId,
          }),
        );
      }

      // Clear selection after swap
      setSelectedWidgetIdState(null);
    },
    [dispatch, sectionsById, widgetInstances, showToast, mode],
  );

  const handleTapReplace = useCallback(
    (targetWidgetId: WidgetId, newWidgetVariantId: string) => {
      if (mode !== ELayoutManagerMode.EDIT || !sectionsById) {
        return;
      }

      // Extract base widget type from variant ID
      const newWidgetType = extractBaseWidgetType(newWidgetVariantId);

      // Get the target widget instance
      const targetWidget = widgetInstances[targetWidgetId];
      if (!targetWidget) {
        return;
      }

      // Find the section containing the target widget
      let targetSectionId: EWidgetSection | null = null;
      let targetIndex = -1;

      for (const [sectionId, section] of Object.entries(sectionsById)) {
        const index = section?.blockOrder?.indexOf(targetWidgetId);
        if (index !== undefined && index !== -1) {
          targetSectionId = sectionId as EWidgetSection;
          targetIndex = index;
          break;
        }
      }

      if (!targetSectionId || targetIndex === -1) {
        return;
      }

      // VALIDATION: Check geo-map placement rules
      if (newWidgetType === 'geo-map') {
        const variantId = newWidgetVariantId;

        // Prevent placing main map (geo-map-center) in side panels
        if (
          variantId === 'geo-map-center' &&
          (targetSectionId === 'left-primary' ||
            targetSectionId === 'right-primary')
        ) {
          showToast(
            'Cannot place Main Map in side panels. Please use Focus View widget for side panels instead.',
            'error',
          );
          return;
        }

        // Prevent placing focus view (geo-map-focus) in center
        if (variantId === 'geo-map-focus' && targetSectionId === 'center') {
          showToast(
            'Cannot place Focus View in center. Use the Main Map widget for the center panel.',
            'error',
          );
          return;
        }
      }

      // Remove old widget
      dispatch(blockActions.blockRemove(targetWidgetId));
      dispatch(
        sectionActions.sectionRemoveBlock({
          sectionId: targetSectionId,
          blockId: targetWidgetId,
        }),
      );
      dispatch(widgetInstanceActions.removeWidgetInstance(targetWidget));

      // Create new widget at the same position
      const newWidgetId = generateWidgetId(newWidgetType);
      const widget = widgetRegistry.get(newWidgetType);

      if (widget) {
        if (newWidgetType === 'new-cases') {
          dispatch(toggleNewCasesBlocks(false));
        }

        dispatch(
          widgetInstanceActions.setWidgetInstance({
            id: newWidgetId,
            title: widget.name,
            sectionId: targetSectionId,
            type: newWidgetType,
            settings: extractWidgetDefaults(widget.config),
          }),
        );
        dispatch(blockActions.blockUpsert({ id: newWidgetId }));
        dispatch(
          sectionActions.sectionAddBlockAtPosition({
            sectionId: targetSectionId,
            widgetType: newWidgetType,
            blockId: newWidgetId,
            position: targetIndex,
          }),
        );
      }

      // Clear selections
      setSelectedPaletteWidgetStateSync(null);
      setSelectedWidgetIdState(null);
    },
    [
      dispatch,
      mode,
      sectionsById,
      widgetInstances,
      showToast,
      setSelectedPaletteWidgetStateSync,
    ],
  );

  const setSelectedWidgetId = useCallback(
    (widgetId: WidgetId | null) => {
      if (mode === ELayoutManagerMode.RENDER) {
        return;
      }
      setSelectedWidgetIdState(widgetId);
    },
    [mode],
  );

  const setSelectedPaletteWidget = useCallback(
    (widgetId: string | null) => {
      if (mode !== ELayoutManagerMode.EDIT) {
        return;
      }

      if (!widgetId) {
        setSelectedPaletteWidgetStateSync(null);
        return;
      }

      const currentPending = pendingPlacementRef.current;

      if (currentPending) {
        // Extract base widget type from variant ID
        const widgetType = extractBaseWidgetType(widgetId);

        // Special handling for geo-map: prevent placing in wrong sections
        if (widgetType === 'geo-map') {
          const variantId = widgetId; // Full variant ID like 'geo-map-center' or 'geo-map-focus'

          // Prevent placing main map (geo-map-center) in side panels
          if (
            variantId === 'geo-map-center' &&
            (currentPending.sectionId === 'left-primary' ||
              currentPending.sectionId === 'right-primary')
          ) {
            showToast(
              'Cannot place Main Map in side panels. Please use Focus View widget for side panels instead.',
              'error',
            );
            setPendingPlacement(null);
            setSelectedPaletteWidgetStateSync(null);
            return;
          }

          // Prevent placing focus view (geo-map-focus) in center
          if (
            variantId === 'geo-map-focus' &&
            currentPending.sectionId === 'center'
          ) {
            showToast(
              'Cannot place Focus View in center. Use the Main Map widget for the center panel.',
              'error',
            );
            setPendingPlacement(null);
            setSelectedPaletteWidgetStateSync(null);
            return;
          }
        }

        const newWidgetId = generateWidgetId(widgetType);
        const widget = widgetRegistry.get(widgetType);
        if (widget) {
          if (widgetType === 'new-cases') {
            dispatch(toggleNewCasesBlocks(false));
          }

          dispatch(
            widgetInstanceActions.setWidgetInstance({
              id: newWidgetId,
              title: widget.name,
              sectionId: currentPending.sectionId,
              type: widgetType,
              settings: extractWidgetDefaults(widget.config),
            }),
          );
          dispatch(blockActions.blockUpsert({ id: newWidgetId }));
          dispatch(
            sectionActions.sectionAddBlockAtPosition({
              sectionId: currentPending.sectionId,
              widgetType,
              blockId: newWidgetId,
              position: currentPending.position,
            }),
          );
        }

        setPendingPlacement(null);
        setSelectedPaletteWidgetStateSync(null);
        return;
      }

      setSelectedPaletteWidgetStateSync(widgetId);
    },
    [
      mode,
      dispatch,
      setPendingPlacement,
      setSelectedPaletteWidgetStateSync,
      showToast,
    ],
  );

  const handleTapPlace = useCallback(
    (sectionId: EWidgetSection, position = 0, widgetId?: string) => {
      if (mode !== ELayoutManagerMode.EDIT) {
        return;
      }

      // If a widget ID is provided directly (e.g. from drag), use it to set as pending?
      // No, handleTapPlace is usually "User clicked here".

      const activeWidgetId = widgetId ?? selectedPaletteWidgetRef.current;

      if (activeWidgetId) {
        // Validate and place immediately
        const widgetType = extractBaseWidgetType(activeWidgetId);

        // Special handling for geo-map
        if (widgetType === 'geo-map') {
          const variantId = activeWidgetId;

          if (
            variantId === 'geo-map-center' &&
            (sectionId === 'left-primary' || sectionId === 'right-primary')
          ) {
            showToast(
              'Cannot place Main Map in side panels. Please use Focus View widget for side panels instead.',
              'error',
            );
            return;
          }

          if (variantId === 'geo-map-focus' && sectionId === 'center') {
            showToast(
              'Cannot place Focus View in center. Use the Main Map widget for the center panel.',
              'error',
            );
            return;
          }
        }

        const newWidgetId = generateWidgetId(widgetType);
        const widget = widgetRegistry.get(widgetType);

        if (widget) {
          if (widgetType === 'new-cases') {
            dispatch(toggleNewCasesBlocks(false));
          }

          dispatch(
            widgetInstanceActions.setWidgetInstance({
              id: newWidgetId,
              title: widget.name,
              sectionId,
              type: widgetType,
              settings: extractWidgetDefaults(widget.config),
            }),
          );
          dispatch(blockActions.blockUpsert({ id: newWidgetId }));
          dispatch(
            sectionActions.sectionAddBlockAtPosition({
              sectionId,
              widgetType,
              blockId: newWidgetId,
              position,
            }),
          );
        }

        setPendingPlacement(null);
        setSelectedPaletteWidgetStateSync(null);
      } else {
        // Just set pending placement
        setPendingPlacement({ sectionId, position });
      }
    },
    [
      mode,
      dispatch,
      setPendingPlacement,
      setSelectedPaletteWidgetStateSync,
      showToast,
    ],
  );

  return {
    selectedWidgetId,
    setSelectedWidgetId,
    selectedPaletteWidget,
    setSelectedPaletteWidget,
    pendingPlacement,
    setPendingPlacement,
    handleTapPlace,
    handleTapReplace,
    handleDeleteWidget,
    handleTapSwap,
  };
};
